// BizTable — DataTables cell renderers for server-side report tables.
//
// A server-side table gets DATA from the API (booleans as booleans, statuses as raw strings) and
// decides presentation itself. These are the recurring cell shapes, taken from a sweep of the 160
// report files: an external link (105 of them), a button (all eight Bootstrap variants in use), a
// badge (114 occurrences), a boolean check, a date (45 files), cased text (62), a number.
//
// Every renderer honours DataTables' orthogonal data model: it returns markup ONLY for
// type === 'display', and the raw value for sort/filter/type. Sorting a status column therefore
// sorts by the stored string, not by the HTML of the badge drawn over it.
//
// Nothing here hard-codes an icon or a colour — those are options with sensible defaults, so a
// report can restyle a column without a new renderer.

type RenderType = 'display' | 'filter' | 'sort' | 'type' | undefined;
type Row = Record<string, any>;

/**
 * Text → HTML-safe text, safe in BOTH text and attribute position.
 *
 * Explicitly maps the five characters rather than round-tripping through
 * textContent → innerHTML: that trick encodes & < > but NOT quotes, so a value containing a
 * quote closes the attribute it sits in and everything after becomes markup. These renderers
 * interpolate into `title="…"` and `href="…"`, and report fields like `label` and `login` are
 * free text an admin types — so the quote cases are the ones that matter here.
 */
function esc(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Only http/https/mailto may become an href. Report URLs are often free text someone typed into
 * an admin form, so a stored `javascript:` URI would run on click — and escaping cannot help,
 * because there the scheme IS the payload.
 */
function safeHref(url: unknown): string | null {
    const value = String(url ?? '').trim();

    // Defence in depth: even correctly escaped, a URL carrying whitespace or angle brackets is
    // not a URL we want to emit — it is a sign the field holds something other than a link.
    if (/[\s"'<>]/.test(value)) {
        return null;
    }

    if (/^(https?|mailto):/i.test(value)) {
        return value;
    }
    return null;
}

/** Resolve `'btn-danger'` or `{quit: 'btn-danger'}` against the cell's value. */
function pick(option: string | Record<string, string> | undefined, value: unknown, fallback: string): string {
    if (typeof option === 'string') {
        return option;
    }
    if (option && typeof option === 'object') {
        return option[String(value).toLowerCase()] ?? fallback;
    }
    return fallback;
}

/** `{id}` / `{practice_id}` in a URL template, filled from the row and URL-encoded. */
function fill(template: string, row: Row): string {
    return template.replace(/\{(\w+)\}/g, (_match, key) => encodeURIComponent(row[key] ?? ''));
}

function iconHtml(icon: string | undefined, extraClass?: string): string {
    if (!icon) {
        return '';
    }
    const cls = extraClass ? `${icon} ${extraClass}` : icon;
    return `<i class="${esc(cls)}"></i>`;
}

export interface LinkOptions {
    /** Row field holding the URL. Defaults to the column's own value. */
    urlField?: string;
    /** URL template instead of a field, e.g. '/ca/customer.php?page=ir&practice_id={practice_id}'. */
    url?: string;
    target?: string;
    /** FA classes prepended inside the link. */
    icon?: string;
    /** Row field to use as the title attribute. */
    titleField?: string;
    /** Cut the visible text at N characters (the title keeps the full value). */
    truncate?: number;
    /** Shown when there is no usable URL. Defaults to the plain text. */
    emptyText?: string;
}

/** An anchor. Falls back to plain text when the URL is missing or not an allowed scheme. */
function link(options: LinkOptions = {}) {
    return function (value: any, type: RenderType, row: Row): any {
        if (type !== 'display') {
            return value;
        }

        const raw = options.url ? fill(options.url, row) : (options.urlField ? row[options.urlField] : value);
        const href = options.url ? raw : safeHref(raw);
        let text = String(value ?? '');

        if (options.truncate && text.length > options.truncate) {
            text = text.slice(0, options.truncate) + '…';
        }

        if (!href) {
            return esc(options.emptyText ?? text);
        }

        const title = options.titleField ? row[options.titleField] : null;
        const attrs = [
            `href="${esc(href)}"`,
            options.target ? `target="${esc(options.target)}"` : '',
            title ? `title="${esc(title)}"` : '',
        ].filter(Boolean).join(' ');

        return `<a ${attrs}>${iconHtml(options.icon)}${iconHtml(options.icon) ? ' ' : ''}${esc(text)}</a>`;
    };
}

export interface ButtonOptions {
    /** URL template, e.g. '/ca/customer.php?page=edit_ir&ir_id={id}'. */
    url: string;
    /** Button text. Defaults to the cell value. */
    label?: string;
    /** 'btn-success', or a value→variant map like {quit: 'btn-danger', hold: 'btn-warning'}. */
    variant?: string | Record<string, string>;
    /** Look the variant map up by this row field instead of the cell value (a practice id coloured by its pi_state). */
    variantField?: string;
    /** Variant when the map has no entry for the value. Defaults to btn-secondary. */
    variantDefault?: string;
    size?: string;
    icon?: string;
    target?: string;
    /** Row field whose non-zero value appends a badge (e.g. a notes count). */
    badgeField?: string;
    badgeVariant?: string;
}

/** A link styled as a button. `variant` may map the cell value to a colour. */
function button(options: ButtonOptions) {
    return function (value: any, type: RenderType, row: Row): any {
        if (type !== 'display') {
            return value;
        }

        const variantKey = options.variantField ? row[options.variantField] : value;
        const variant = pick(options.variant, variantKey, options.variantDefault ?? 'btn-secondary');
        const size = options.size ?? 'btn-sm';
        const label = options.label ?? String(value ?? '');

        let badge = '';
        if (options.badgeField && Number(row[options.badgeField]) > 0) {
            const badgeVariant = options.badgeVariant ?? 'bg-light text-dark';
            badge = ` <span class="badge ${esc(badgeVariant)}">${esc(row[options.badgeField])}</span>`;
        }

        const target = options.target ? ` target="${esc(options.target)}"` : '';

        return `<a href="${esc(fill(options.url, row))}"${target} class="btn ${esc(size)} ${esc(variant)}">`
            + `${iconHtml(options.icon)}${options.icon ? ' ' : ''}${esc(label)}${badge}</a>`;
    };
}

export interface BadgeOptions {
    /** 'bg-success', or a value→variant map. */
    variant?: string | Record<string, string>;
    icon?: string;
    /** Value→label map, when the stored value is not what should be shown. */
    labels?: Record<string, string>;
    /** Render nothing for an empty value rather than an empty badge. */
    hideEmpty?: boolean;
}

function badge(options: BadgeOptions = {}) {
    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }
        if (options.hideEmpty && (value === null || value === undefined || value === '')) {
            return '';
        }

        const variant = pick(options.variant, value, 'bg-secondary');
        const label = options.labels?.[String(value).toLowerCase()] ?? String(value ?? '');

        return `<span class="badge ${esc(variant)}">${iconHtml(options.icon)}${options.icon ? ' ' : ''}${esc(label)}</span>`;
    };
}

export interface CheckmarkOptions {
    trueIcon?: string;
    falseIcon?: string;
    trueClass?: string;
    falseClass?: string;
    /** Text beside the icon. Omit for icon-only. */
    trueLabel?: string;
    falseLabel?: string;
    /** Render nothing at all for false, rather than a red cross. */
    hideFalse?: boolean;
}

/**
 * A yes/no cell drawn as an icon. Named for what it PRODUCES, not what it takes — `bool` would
 * describe the input, and the renderer's job is the output.
 *
 * Defaults to the green check / red cross already used across the reports.
 */
function checkmark(options: CheckmarkOptions = {}) {
    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }

        const on = value === true || value === 1 || value === '1' || value === 'true';
        if (!on && options.hideFalse) {
            return '';
        }

        const icon = on
            ? (options.trueIcon ?? 'fa fa-check-circle fa-2x')
            : (options.falseIcon ?? 'fa fa-times-circle fa-2x');
        const cls = on
            ? (options.trueClass ?? 'green_text')
            : (options.falseClass ?? 'red_text');
        const label = on ? options.trueLabel : options.falseLabel;

        return `<i class="${esc(icon)} ${esc(cls)}"></i>${label ? ' ' + esc(label) : ''}`;
    };
}

export interface DateOptions {
    /** 'Y-m-d'-style tokens are NOT used — this takes an Intl locale string, or 'iso'. */
    locale?: string;
    intl?: Intl.DateTimeFormatOptions;
    /** Shown for a null/empty/unparseable date. */
    empty?: string;
}

/**
 * A date cell. Display is formatted; sort and filter get the RAW value, so ordering stays
 * chronological rather than alphabetical on the formatted string — the whole point of the
 * orthogonal-data model.
 */
function date(options: DateOptions = {}) {
    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }
        if (!value) {
            return esc(options.empty ?? '');
        }

        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) {
            return esc(String(value));
        }

        return esc(parsed.toLocaleDateString(
            options.locale ?? undefined,
            options.intl ?? { year: 'numeric', month: 'short', day: 'numeric' }
        ));
    };
}

export interface TextOptions {
    transform?: 'ucwords' | 'ucfirst' | 'upper' | 'lower';
    truncate?: number;
    empty?: string;
    /** Wrap in a class, e.g. 'text-muted'. */
    className?: string;
}

function text(options: TextOptions = {}) {
    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }

        let out = String(value ?? '');
        if (out === '') {
            out = options.empty ?? '';
        }

        if (options.transform === 'ucwords') {
            out = out.replace(/\b\w/g, (c) => c.toUpperCase());
        } else if (options.transform === 'ucfirst') {
            out = out.charAt(0).toUpperCase() + out.slice(1);
        } else if (options.transform === 'upper') {
            out = out.toUpperCase();
        } else if (options.transform === 'lower') {
            out = out.toLowerCase();
        }

        const full = out;
        if (options.truncate && out.length > options.truncate) {
            out = out.slice(0, options.truncate) + '…';
        }

        if (options.className) {
            return `<span class="${esc(options.className)}"${full !== out ? ` title="${esc(full)}"` : ''}>${esc(out)}</span>`;
        }
        return full !== out ? `<span title="${esc(full)}">${esc(out)}</span>` : esc(out);
    };
}

export interface NumberOptions {
    decimals?: number;
    prefix?: string;
    suffix?: string;
    /** Shown for null/empty — not for a real zero. */
    empty?: string;
    locale?: string;
}

function number(options: NumberOptions = {}) {
    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }
        if (value === null || value === undefined || value === '') {
            return esc(options.empty ?? '');
        }

        const n = Number(value);
        if (isNaN(n)) {
            return esc(String(value));
        }

        const formatted = n.toLocaleString(options.locale ?? undefined, {
            minimumFractionDigits: options.decimals ?? 0,
            maximumFractionDigits: options.decimals ?? 0,
        });

        return esc((options.prefix ?? '') + formatted + (options.suffix ?? ''));
    };
}

export interface HeatOptions {
    /** The value that reaches full green. 0 is red; between, the hue climbs through orange and yellow. */
    max?: number;
}

/**
 * A count as a heatmap badge — red at 0, shading to green at `max` and above. For "how much of X
 * does each row have", where an all-grey column of numbers says nothing at a glance.
 *
 * The colour is computed per value, so it is an inline style — no class covers a scale.
 */
function heat(options: HeatOptions = {}) {
    const max = options.max ?? 100;

    return function (value: any, type: RenderType): any {
        if (type !== 'display') {
            return value;
        }

        const count = Number(value) || 0;
        let hue = 0;
        if (count > 0) {
            hue = 20 + Math.min(count / max, 1) * 100;   // 20 (orange) → 120 (green)
        }

        return `<span class="badge" style="background-color: hsl(${Math.round(hue)}, 70%, 40%)">${esc(String(count))}</span>`;
    };
}

export const bizTable = {
    link,
    button,
    badge,
    checkmark,
    date,
    text,
    number,
    heat,
    // Exposed because report-specific renderers need the same guarantees.
    esc,
    safeHref,
};
